import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Stack,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  CircularProgress,
  Alert,
  AlertTitle,
  Divider,
  Chip,
  Tooltip,
  IconButton,
} from '@mui/material';
import { Add, AssignmentInd, ContentCopy } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { rolesAPI } from '../../../roles/services/roles';
import { staffAPI } from '../../../staff/services/staff';
import { useSnackbar } from '../../../../hooks/mui/useSnackBar';
import { getApiStatus } from '../../utils/apiErrors';
import LoadErrorAlert from '../LoadErrorAlert';

// Random 8-digit numeric password for new staff members
const generatePassword = () => {
  const digits = new Uint32Array(8);
  crypto.getRandomValues(digits);
  return Array.from(digits, d => d % 10).join('');
};

export default function Step2StaffAssignments({ onRefresh }) {
  const { t } = useTranslation();
  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [staffRoles, setStaffRoles] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [saving, setSaving] = useState(false);
  const [togglingCell, setTogglingCell] = useState(null);
  const [assigningRoleId, setAssigningRoleId] = useState(null);
  // Passwords are only kept in memory and disappear when the user leaves this step
  const [createdCredentials, setCreatedCredentials] = useState([]);
  const { showSuccess, showError } = useSnackbar();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [staffWithRolesRes, rolesRes] = await Promise.all([
        staffAPI.getDepartmentStaffWithRoles(),
        rolesAPI.getDepartmentRoles(),
      ]);
      const staffList = staffWithRolesRes.data?.staff_members || [];
      const rolesList = rolesRes.data?.roles || [];
      setStaff(staffList);
      setRoles(rolesList);

      const rolesMap = {};
      staffList.forEach(s => {
        rolesMap[s.staff_member_id] = new Set((s.roles || []).map(r => r.role_id));
      });
      setStaffRoles(rolesMap);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showSuccess(t('onboarding:shared.copied'));
    } catch {
      showError(t('onboarding:shared.copy_failed'));
    }
  };

  const handleAddStaff = async () => {
    const trimmed = name.trim();
    if (!trimmed) { setNameError(t('onboarding:step2.errors.name_required')); return; }

    setSaving(true);
    const password = generatePassword();
    try {
      const res = await staffAPI.createStaff({ name: trimmed, password });
      setCreatedCredentials(prev => [
        ...prev,
        {
          staff_member_id: res.data?.staff_member_id ?? `${trimmed}-${prev.length}`,
          name: res.data?.name ?? trimmed,
          password,
        },
      ]);
      setName('');
      setNameError('');
      setShowAddForm(false);
      showSuccess(t('onboarding:step2.success.created'));
      await fetchData();
      onRefresh();
    } catch (e) {
      showError(getApiStatus(e) === 409
        ? t('onboarding:step2.errors.duplicate_name')
        : t('onboarding:step2.errors.create_failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleRole = async (staffId, roleId, currentlyAssigned) => {
    const cellKey = `${staffId}:${roleId}`;
    setTogglingCell(cellKey);
    try {
      if (currentlyAssigned) {
        await staffAPI.removeRole(staffId, roleId);
      } else {
        await staffAPI.assignRole(staffId, { role_id: roleId });
      }
      setStaffRoles(prev => {
        const next = { ...prev };
        const set = new Set(prev[staffId] || []);
        currentlyAssigned ? set.delete(roleId) : set.add(roleId);
        next[staffId] = set;
        return next;
      });
      onRefresh();
    } catch {
      showError(t('onboarding:step2.errors.toggle_failed'));
    } finally {
      setTogglingCell(null);
    }
  };

  const handleAssignRoleToAll = async (role) => {
    const unassigned = staff.filter(s => !staffRoles[s.staff_member_id]?.has(role.role_id));
    if (!unassigned.length) {
      showSuccess(t('onboarding:step2.success.already_assigned', { name: role.name }));
      return;
    }

    setAssigningRoleId(role.role_id);
    try {
      const results = await Promise.allSettled(
        unassigned.map(s => staffAPI.assignRole(s.staff_member_id, { role_id: role.role_id }))
      );
      const assignedIds = unassigned
        .filter((_, i) => results[i].status === 'fulfilled')
        .map(s => s.staff_member_id);
      const failedCount = unassigned.length - assignedIds.length;

      if (assignedIds.length) {
        setStaffRoles(prev => {
          const next = { ...prev };
          assignedIds.forEach(id => {
            const set = new Set(prev[id] || []);
            set.add(role.role_id);
            next[id] = set;
          });
          return next;
        });
        onRefresh();
      }

      if (failedCount) {
        showError(t('onboarding:step2.errors.assign_all_partial', { name: role.name, count: failedCount }));
      } else {
        showSuccess(t('onboarding:step2.success.assigned_all', { name: role.name }));
      }
    } finally {
      setAssigningRoleId(null);
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        {t('onboarding:step2.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {t('onboarding:step2.subtitle')}
      </Typography>

      {/* Add staff */}
      <Button
        variant="contained"
        startIcon={<Add />}
        onClick={() => setShowAddForm(v => !v)}
        size="small"
        sx={{ mb: 2 }}
      >
        {t('onboarding:step2.add_staff')}
      </Button>

      {showAddForm && (
        <Box
          sx={{
            p: 2, mb: 2, border: 1, borderColor: 'divider',
            borderRadius: 1, bgcolor: 'background.paper',
          }}
        >
          <Stack spacing={2}>
            <TextField
              label={t('onboarding:step2.form_name')}
              size="small"
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !saving) handleAddStaff(); }}
              error={!!nameError}
              helperText={nameError || t('onboarding:step2.password_hint')}
              autoFocus
            />
            <Stack direction="row" spacing={1}>
              <Button variant="contained" size="small" onClick={handleAddStaff} disabled={saving}>
                {saving ? <CircularProgress size={16} /> : t('common:buttons.save')}
              </Button>
              <Button size="small" onClick={() => { setShowAddForm(false); setNameError(''); }}>
                {t('common:buttons.cancel')}
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}

      {createdCredentials.length > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<ContentCopy />}
              onClick={() => copyText(createdCredentials.map(c => `${c.name}: ${c.password}`).join('\n'))}
            >
              {t('onboarding:shared.copy_all')}
            </Button>
          }
        >
          <AlertTitle>{t('onboarding:step2.credentials_title')}</AlertTitle>
          <Typography variant="body2" mb={1}>
            {t('onboarding:step2.credentials_hint')}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('onboarding:step2.staff_column')}</TableCell>
                <TableCell>{t('onboarding:step2.credentials_password')}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {createdCredentials.map(c => (
                <TableRow key={c.staff_member_id}>
                  <TableCell>{c.name}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '1rem', letterSpacing: 1 }}>
                    {c.password}
                  </TableCell>
                  <TableCell align="right" padding="checkbox">
                    <Tooltip title={t('onboarding:shared.copy')}>
                      <IconButton size="small" onClick={() => copyText(c.password)}>
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      ) : loadError ? (
        <LoadErrorAlert onRetry={fetchData} />
      ) : staff.length === 0 ? (
        <Alert severity="info">
          {t('onboarding:step2.no_staff')}
        </Alert>
      ) : roles.length === 0 ? (
        <Alert severity="warning">
          {t('onboarding:step2.no_roles')}
        </Alert>
      ) : (
        <>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" color="text.secondary" mb={1}>
            {t('onboarding:step2.role_assignment')}
          </Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>{t('onboarding:step2.staff_column')}</TableCell>
                  {roles.map(role => (
                    <TableCell key={role.role_id} align="center" sx={{ minWidth: 140 }}>
                      <Stack spacing={0.5} alignItems="center">
                        <Typography variant="caption" fontWeight={700}>
                          {role.name}
                        </Typography>
                        {assigningRoleId === role.role_id ? (
                          <CircularProgress size={18} sx={{ my: 0.75 }} />
                        ) : (
                          <Tooltip title={t('onboarding:step2.assign_all_tooltip', { name: role.name })}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleAssignRoleToAll(role)}
                                disabled={!!assigningRoleId}
                                color="primary"
                              >
                                <AssignmentInd fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                          {t('onboarding:step2.assign_all')}
                        </Typography>
                      </Stack>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {staff.map(s => (
                  <TableRow key={s.staff_member_id}>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2">{s.name}</Typography>
                        {!s.is_active && (
                          <Chip label={t('onboarding:step2.inactive')} size="small" color="default" />
                        )}
                      </Stack>
                    </TableCell>
                    {roles.map(role => {
                      const cellKey = `${s.staff_member_id}:${role.role_id}`;
                      const assigned = staffRoles[s.staff_member_id]?.has(role.role_id) || false;
                      return (
                        <TableCell key={role.role_id} align="center">
                          {togglingCell === cellKey ? (
                            <CircularProgress size={18} />
                          ) : (
                            <Checkbox
                              checked={assigned}
                              size="small"
                              disabled={assigningRoleId === role.role_id}
                              onChange={() => handleToggleRole(s.staff_member_id, role.role_id, assigned)}
                            />
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </>
      )}
    </Box>
  );
}
